/**
 * Canasta básica PROFECO — los 24 productos del PACIC (Paquete Contra la
 * Inflación y la Carestía), vigente en 2026 con tope de $910 para el conjunto.
 *
 * Cada concepto trae reglas para reconocer en el catálogo los productos que
 * SÍ son ese básico ("Aceite Mixto 800 ml") y descartar los que sólo lo
 * mencionan ("Atún en aceite", "Aceite 10W-30", "Huevo sorpresa"). El script
 * `scripts/mark-canasta-basica.ts` usa `classifyPacic` para poner
 * `Product.isBasicBasket`, y `/comparador/canastas` lo usa para armar listas.
 *
 * Además de los 24 oficiales hay "complementos" (café, sal, detergente…) que
 * entran en algunas canastas de SuCom pero NO se marcan como canasta básica.
 */

export type BaseUnit = "kg" | "L" | "pz";

export interface BasketConcept {
  slug: string;
  /** Nombre corto para la lista de compras. */
  label: string;
  /** Nombre como lo publica PROFECO (sólo conceptos PACIC). */
  profecoName?: string;
  /** true = uno de los 24 del PACIC; false = complemento de SuCom. */
  pacic: boolean;
  glyph: string;
  /** Unidad en la que se compara el precio y se mide la cantidad. */
  unit: BaseUnit;
  /** Tamaño supuesto cuando el producto no trae uno compatible con `unit`. */
  typicalPack: number;
  /** Términos (sin acentos, ≥3 letras) para prefiltrar en SQL. */
  searchTerms: string[];
  /** Al menos uno debe coincidir con nombre + marca normalizados. */
  include: RegExp[];
  /** Ninguno puede coincidir. */
  exclude?: RegExp[];
  /** Fruta/verdura fresca: exige que el texto empiece con el nombre o que sea a granel. */
  produce?: boolean;
}

export function normalizeForMatch(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9.\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const w = (pattern: string) => new RegExp(`\\b(?:${pattern})\\b`);

/** Nada de esto es despensa, sin importar qué palabra traiga. */
const GLOBAL_EXCLUDE = w(
  [
    "juguetes?", "jugueteria", "coleccionables?", "sorpresa", "peluche", "disfraz", "figura", "llavero",
    "slime", "plastilina", "squishy", "zuru", "comida falsa", "de juguete", "interactivo",
    "perros?", "gatos?", "mascotas?", "felinos?", "caninos?", "croquetas?", "whiskas", "pedigree",
    "felix", "purina", "dog chow", "cat chow", "nupec", "minino",
    "veladoras?", "velas?", "aromatizantes?", "ambientador", "perfume", "colonia",
    "kit de cultivo", "para sembrar", "germinador", "macetas?", "planta artificial", "hortaflor",
    "carnada", "gorras?", "playeras?", "camisas?", "libros?", "agenda", "hervidor", "cortador", "cestas?",
    "difusor", "humidificador", "palillos", "onigiri",
  ].join("|"),
);

/**
 * Categorías de tienda que nunca son despensa. Se comparan COMPLETAS: "Granos
 * y semillas" (arroz) no debe caer por parecerse a "Semillas" (para sembrar).
 */
const GLOBAL_EXCLUDE_CATEGORY = new RegExp(
  `^(?:${[
    "coleccionables", "juguetes", "pascua", "primavera", "variedad", "manualidades", "dinosaurios y animales",
    "mascotas", "semillas", "plantas y flores naturales", "automotriz", "autos y motocicletas",
    "aceites y anticongelantes", "otros liquidos", "herramientas", "moldes", "mesa", "utensilios de cocina",
    "aromatizantes", "aromatizantes y veladoras", "aromaterapia", "decoracion", "decoracion de muebles",
    "electronica", "ropa", "calzado", "ferreteria", "trapeadores y mops", "cuidado y limpieza",
    "dermatologicos", "cremas y aceites corporales", "cremas corporales", "cremas y lociones", "bronceadores",
    "tratamientos", "tratamientos capilares", "cuidado capilar", "shampoos", "acondicionadores", "acondicionador",
    "facial", "naturales", "maquillaje para ojos", "medicamentos", "estomacales", "vitaminas y suplementos",
    "cuidado femenino", "accesorios para maternidad", "contenedores y recolectores de leche",
    "higiene y cuidado del bebe", "cereales y galletas para bebe", "cervezas importadas", "licores",
    "dulceria", "chocolates", "pan dulce", "galletas", "galletas de arroz", "saborizantes", "saborizantes de agua",
    "caldos y concentrados", "especias y condimentos", "sazonadores", "alimentos preparados", "comida preparada",
    "otras carnes frias", "pasteles gelatinas y postres", "pasteles pays y panques refrigerados",
  ].join("|")})$`,
);

/** Productos procesados que nunca cuentan como fruta o verdura fresca. */
const PROCESSED = w(
  [
    "jugos?", "nectar", "nectares", "pures?(?! harvest)", "papillas?", "gerber", "compotas?", "mermeladas?", "salsas?",
    "sabor", "sabores", "saborizad[oa]", "sazonador", "consome", "caldo", "caldillo", "sopas?", "knorr", "maggi", "sazonador[ae]?s?",
    "deshidratad[oa]s?", "secos?", "en polvo", "polvo", "chips", "botana", "frit[oa]s?", "congelad[oa]s?",
    "en lata", "lata", "enlatad[oa]s?", "escabeche", "encurtid[oa]s?", "en vinagre", "vinagre", "rajas",
    "pasta", "pastel", "pay", "galletas?", "pan", "harina", "yogh?urt", "gelatina", "helado", "paleta",
    "cereal", "barra", "te", "refresco", "bebida", "agua", "sidral", "concentrado", "esencia", "aceite",
    "shampoo", "sh", "jabon", "crema", "mascarilla", "gel", "acondicionador", "bloqueador", "desodorante",
    "limpiador", "multiusos", "desinfectante", "lavatrastes", "cloro", "toallitas",
    "(?<!sin |con )semillas?", "kit", "planta", "pizza", "aderezo", "dip", "ketchup", "catsup", "clamato", "michelada",
    "caramelos?", "dulces?", "chamoy", "tajin", "cacahuates?", "papitas", "pringles", "sabritas",
    "mezcla", "verduras mixtas", "picadillo", "macedonia", "molid[oa]s?", "condimentad[oa]s?", "pelad[oa]s?",
    "cubos", "pomodorini", "cirio",
  ].join("|"),
);

const BULK_PRODUCE = w("kg|kilo|kilos|por kilo|granel|a granel|pieza|piezas|pz|pza|malla|bolsa|organic[oa]s?|frescos?|frescas?");

export const PACIC_CONCEPTS: BasketConcept[] = [
  {
    // Antes de "aceite" para que "Atún en aceite" no cuente como aceite.
    slug: "atun", label: "Atún en lata", profecoName: "Atún en hojuelas", pacic: true, glyph: "🐟",
    unit: "kg", typicalPack: 0.14, searchTerms: ["atun"],
    include: [/\batun\b.*\b(?:agua|aceite|hojuelas?|trozos?|solido|lata|desmenuzado|natural|oliva|ventresca|aleta amarilla|blanco|claro)\b/],
    exclude: [w("fresco|filete|lomo fresco|steak|congelado|sabor|pate|sushi|ahi|ensalada|mayonesa|aderezo|vinagreta|chipotle|carnitas|tajin|churu|verduras|tomate|almeja|habanero|jalapeno|picante|salsa|botana|tostadas?|empanad\\w*|limon|elote|pasta|escabeche|suna|sakudori|medallon(?:es)?|cubos|cubitos|hamburguesa|sticks|poke|congelad[oa]|ahumado|rellen[oa]s?|arroz|hot pepper|sport")],
  },
  {
    slug: "sardina", label: "Sardina en tomate", profecoName: "Sardina en tomate (lata)", pacic: true, glyph: "🐟",
    unit: "kg", typicalPack: 0.425, searchTerms: ["sardina"],
    include: [/\bsardinas?\b.*\b(?:tomate|jitomate)\b|\b(?:tomate|jitomate)\b.*\bsardinas?\b/],
    exclude: [w("fresca|congelada")],
  },
  {
    slug: "aceite", label: "Aceite vegetal", profecoName: "Aceite vegetal comestible", pacic: true, glyph: "🛢️",
    unit: "L", typicalPack: 1, searchTerms: ["aceite"],
    include: [
      /\baceite\b.*\b(?:vegetal|mixto|comestible|maiz|canola|soya|soja|girasol|cartamo|1 2 3|nutrioli|capullo|cristal|patrona|kartamus|mazola|oleico|ideal|great value|gran dia|members mark|pepita de uva)\b/,
      /\baceite de uva\b.*\b\d{3,4}\s?ml\b/,
      /\b(?:nutrioli|capullo|kartamus|mazola|1 2 3)\b.*\b\d+(?:\.\d+)?\s?(?:ml|l|lt|lts|litros?)\b/,
    ],
    exclude: [
      w("oliva|aguacate|coco|ajonjoli|sesamo|linaza|almendra|nuez|trufa|chile|aceitunas?|motor|10w|15w|20w|5w|sae|2t|4t|lubricante|transmision|hidraulico|anticongelante|esencial|esenciales|bebe|baby|corporal|masaje|capilar|cabello|cuerpo|aromaterapia|aerosol|spray|vinagre|balsamico|atun|sardina|en aceite|cbd|ricino|mineral|cocina antiadherente|freidora|baking"),
    ],
  },
  {
    slug: "arroz", label: "Arroz", profecoName: "Arroz en grano", pacic: true, glyph: "🍚",
    unit: "kg", typicalPack: 1, searchTerms: ["arroz"],
    include: [w("arroz")],
    exclude: [
      w("papel|horchata|leche|bebida|galletas?|tortillas?|pasta|fideos?|vinagre|sazonador|harina|inflado|cereal|crispy|pastel|tostadas?|tostaditas?|tortitas?|totopos|snack|mochi|salvado|crema|sake|cerveza|proteina|polvo|sopa|caldo|listo|microondas|instantaneo|con pollo|a la|mexicana|poblana|primavera|jardinera|espanol|espanola|yakimeshi|frito|preparado|verduras|elote|condiment\\w*|tomate|jitomate|knorr|bens|mix|tamales?|dulce|paleta|chocolate|obleas|pudin|arrocera|vaporera|aceite|cuchara|vermicelli|coliflor|morcilla|palmini|rainbow|gerber|moldes?|cucharas?|prensa|maquina|bento|juego|sets?|contenedor|mezcla|funghi|risotto|hongos"),
    ],
  },
  {
    slug: "azucar", label: "Azúcar", profecoName: "Azúcar estándar", pacic: true, glyph: "🍬",
    unit: "kg", typicalPack: 1, searchTerms: ["azucar"],
    include: [/^azucar\b/, w("azucar (?:estandar|refinada|morena|mascabad[oa]|blanca|organica|de cana|turbinad[ao]|rubia|glass|refinada)")],
    exclude: [w("sin azucar|bajo en azucar|reducid[oa]|donas?|pan|galletas?|cereal|chicles?|caramelos?|bebida|refresco|jugo|sustituto|splenda|stevia|equal|canderel|algodon|cubierta|recubiert[oa]s?|con azucar|sobres?|sticks?|cubos?|tabletas?|avena|liviant|sin calorias|betty crocker|coca cola|\\d{3,4} pzas")],
  },
  {
    slug: "carne-res", label: "Carne de res", profecoName: "Carne de res", pacic: true, glyph: "🥩",
    unit: "kg", typicalPack: 1, searchTerms: ["res", "bistec", "arrachera", "sirloin", "molida", "diezmillo", "aguayon", "ribeye"],
    include: [
      /\bcarne (?:de )?res\b/,
      /\bres\b.*\b(?:kg|kilo|bistec|bistek|molida|corte|filete|costilla|pulpa|milanesa|cubos|trozo|falda|chambarete)\b/,
      /\bmolida de res\b|\bcarne molida\b/,
      /\bnew york\b.*\b(?:res|kg|angus|choice|prime)\b/,
      w("bistec|bistek|arrachera|sirloin|ribeye|rib eye|t bone|diezmillo|aguayon|chambarete|suadero|picana|tomahawk|cowboy|pulpa negra|pulpa bola|top sirloin|short rib"),
    ],
    exclude: [w("cerdo|puerco|pollo|pavo|pescado|atun|soya|vegetal|plant based|vegana?|caldo|consome|sabor|sopa|salchichas?|jamon|chorizo|machaca|cecina enlatada|hamburguesas?|albondigas?|cubos? knorr|sazonador|enlatad[oa]|lata|chicharron|empanizad[oa]|nuggets?|deshebrada|tamales?|pizza|empanadas?|lasagna|precocid[oa]|congelad[oa] listo|marinador|sazon|pepperoni|salsa|ragu|bocadillo|jerky|menudo|panza|pata|tripas?|higado|lengua|sesos|mondongo|tuetano|cocid[oa]")],
  },
  {
    slug: "cerdo", label: "Carne de cerdo", profecoName: "Carne de cerdo", pacic: true, glyph: "🥓",
    unit: "kg", typicalPack: 1, searchTerms: ["cerdo", "puerco", "chuleta", "pernil", "espaldilla"],
    include: [w("cerdo|puerco|pernil|espaldilla"), /\bchuleta\b(?! de res)/],
    exclude: [w("jamon|salchichas?|chorizo|tocino|chicharron|manteca|pate|embutido|sabor|caldo|consome|carnitas|cueritos|queso de puerco|longaniza|enlatad[oa]|lata|res|pollo|pavo|tamales?|pozole|pizza|empanadas?|salami|pepperoni|mortadela|botana|cerdo vietnamita|alcancia|cochinita|pibil|chilorio|pastor|ahumad[oa]|navidena|selva negra|capitalista")],
  },
  {
    slug: "pollo", label: "Carne de pollo", profecoName: "Carne de pollo", pacic: true, glyph: "🍗",
    unit: "kg", typicalPack: 1, searchTerms: ["pollo", "pechuga", "muslo"],
    include: [
      /\bpollo\b.*\b(?:entero|pechugas?|piernas?|muslos?|alas?|alitas|milanesa|filete|fajitas|kg|kilo|fresco|piezas|cuarto|retazo|huacal|molida|deshuesad[oa]|sin hueso|naturalmente)\b/,
      /\b(?:pechugas?|piernas?|muslos?|alas?|alitas|milanesa|filete|fajitas)\b.*\bpollo\b/,
      /^pollo\b/,
    ],
    exclude: [w("caldo|consome|sabor|knorr|maggi|cubos|sopa|salchichas?|jamon|nuggets?|empanizad[oa]s?|rostizado|pate|enlatad[oa]|lata|huevos?|tortillas?|chips|botana|sazonador|polvo|instantanea|tamales?|pizza|empanadas?|tinga|mole|pavo|soya|vegetal|plant based|alimento para|sandwich|burrito|lasagna|precocid[oa]|boneless|cordon bleu|dedos|tiras empanizadas|marinador|sazon|de pollo listo|para pollo|pollo loco|popcorn|tender|spicy|yakimeshi|banad[oa]|bufalo|buffalo|lemon pepper|salsa|medallon|bbq|teriyaki|crispy|omelette")],
  },
  {
    slug: "frijol", label: "Frijol", profecoName: "Frijol negro", pacic: true, glyph: "🫘",
    unit: "kg", typicalPack: 1, searchTerms: ["frijol"],
    include: [w("frijol|frijoles")],
    exclude: [w("refritos?|enlatad[oa]s?|lata|sopa|chips|botana|harina|crema|caldo|charros?|con chorizo|preparad[oa]s?|listos?|salsa|tostadas?|germinad[oa]|ejotero|de la olla|bote|pouch|tamales?|enfrijoladas|puree|rancheros?|ref|molidos?|fideos?|totopos|chili|spam|con chile|isadora|enteros?|colados?|ranch|puercos|sin grasa|sin frijoles")],
  },
  {
    slug: "huevo", label: "Huevo", profecoName: "Huevo de gallina", pacic: true, glyph: "🥚",
    unit: "pz", typicalPack: 18, searchTerms: ["huevo"],
    include: [w("huevos?")],
    exclude: [w("kinder|chocolate|pascua|dinosaurio|hatch|smashers|rainbocorns|pelota|gelatina|pasta al huevo|pasta|fideos?|tallarin|galletas?|mayonesa|en polvo|polvo|liquido|claras|codorniz|cocedor|molde|separador|batidor|huevera|cartera|dispensador|pan|dulce|surprise|decorativo|de pascua|shampoo|mascarilla|tratamiento|crema|reposteria|sustituto|vegano|clara de huevo|ponche|rompope|ponedora|incubadora|bote|cascaron|liquid[oa]|claras?|revuelto|flautas?|cema|contenedor(?:es)?|organizador|lego|masturbador|hatchimals|sin huevo|queso|yoshi|omelette|happy sweet|princess|\\d+(?:\\.\\d+)? ?(?:grs?|g)")],
  },
  {
    slug: "jabon", label: "Jabón de tocador", profecoName: "Jabón de tocador", pacic: true, glyph: "🧼",
    unit: "pz", typicalPack: 1, searchTerms: ["jabon"],
    include: [
      /\bjabon\b.*\b(?:tocador|barra|en barra|corporal en barra)\b/,
      /\bjabon\b.*\b(?:dove|palmolive|escudo|camay|lux|zest|nivea|protex|rexona|neutro balance|asepxia|maja|heno de pravia|grisi|lirio de tocador|dial|safeguard|irish spring|neutrogena)\b/,
      /\b(?:dove|palmolive|escudo|camay|lux|zest|protex|maja|grisi|dial|safeguard)\b.*\bjabon\b/,
    ],
    exclude: [w("liquido|lavanderia|ropa|zote|lirio|roma|ariel|foca|persil|ace|polvo|trastes|lavatrastes|detergente|intimo|espuma|gel|recarga|dispensador|jabonera|para manos liquido|shampoo|mascota|refill|ml")],
  },
  {
    slug: "leche", label: "Leche", profecoName: "Leche de vaca", pacic: true, glyph: "🥛",
    unit: "L", typicalPack: 1, searchTerms: ["leche"],
    include: [
      /\bleche\b.*\b(?:entera|semidescremada|semi|descremada|light|deslactosada|sin lactosa|ultrapasteurizada|pasteurizada|uht|fresca|clasica|vaca|light|libre de lactosa|baja en grasa|organica|a2|liconsa)\b/,
      /^leche\b.*\b\d+(?:\.\d+)?\s?(?:ml|l|lt|lts|litros?)\b/,
    ],
    exclude: [w("saborizada|sabor|fresa|chocolate|vainilla|cafe|choco|yomi|condensada|evaporada|en polvo|polvo|almendra|soya|soja|avena|coco|arroz|vegetal|ades|alpro|silk|cabra|formula|infantil|nan|enfamil|similac|etapa|crecimiento|nido|kinder|magnesia|corporal|limpiadora|desmaquillante|bloqueador|solar|hidratante|capilar|shampoo|jabon|bebida|cajeta|dulce|helado|nieve|pan|galletas?|yogh?urt|queso|mantequilla|crema|licor|rompope|cereal|malteada|cacao|whey|piel|facial|cuerpo|body|tratamiento|mascarilla|de burra|corporal|bano|baño|leche de|leche y|con leche|cajeta|chocomilk|nesquik|caramelo|carlos v|brownie|capuchino|cappuccino|marsella|forti|fortileche|forti plus|amanecer|alimento lacteo|producto lacteo|formula lactea|bebida lactea|chocomenta|menta")],
  },
  {
    slug: "pan-caja", label: "Pan blanco de caja", profecoName: "Pan blanco de caja", pacic: true, glyph: "🍞",
    unit: "kg", typicalPack: 0.68, searchTerms: ["pan blanco", "pan de caja", "pan caja", "pan grande", "pan sandwich", "pan molde"],
    include: [
      /\bpan (?:blanco|super blanco|de caja|caja|grande|sandwich|para sandwich|de molde|molde|en barra|artesano)\b/,
      /\bpan\b.*\ben barra\b/,
      /\bpan de rinde\b/,
      /^wonder\b.*\b(?:567|680) ?grs?\b/,
    ],
    exclude: [w("integral|multigrano|linaza|\\d+ granos|avena|masa madre|centeno|molido|tostad[oa]s?|dulce|roles|donas?|conchas?|bollos?|hamburguesas?|hot ?dog|thins|tortillas?|pita|arabe|baguette|bolillos?|teleras?|muffins?|croissant|galletas?|pastel|panque|mantecada|polvo para|harina|mezcla|moldes? para|pan de muerto|rosca|pan de elote|crema|sin corteza para hornear|zote|jabon")],
  },
  {
    slug: "pasta", label: "Pasta para sopa", profecoName: "Pasta para sopa", pacic: true, glyph: "🍝",
    unit: "kg", typicalPack: 0.2, searchTerms: ["pasta", "sopa", "fideo", "codo", "espagueti", "spaghetti"],
    include: [
      /\b(?:pasta|sopa)\b.*\b(?:fideos?|codos?|coditos?|estrellas?|municion|letras?|alfabeto|conchas?|conchitas?|corbatas?|mono|monos|engranes?|espagueti|spaghetti|tallarin|macarron|penne|fusilli|plumas?|tornillos?|caracol|cabello de angel|ojo de perdiz|semilla de melon|anillos?|lenguas?|rigatoni|lasagna|farfalle|linguine|fettuccine)\b/,
      /^(?:fideos?|codos?|coditos?|espagueti|spaghetti|macarron|tallarin|spaghetti)\b/,
      /\b(?:la moderna|barilla|yemina|italpasta|la italiana|great value|del monte|knorr)\b.*\b(?:fideos?|codos?|espagueti|spaghetti|macarron|estrellas?|letras?|municion)\b/,
    ],
    exclude: [w("dental|dientes|colgate|sensodyne|crest|oral|de tomate|de chile|de achiote|para pegar|resanar|modelar|pared|madera|instantanea|maruchan|nissin|ramen|cup|vaso|sabor|caldo|preparad[oa]|lista|microondas|udon|konjac|arroz|cocida|soba|precocida|enlatad[oa]|lata|con queso|mac and cheese|kraft|salsa|tomate y|con tomate|de pollo|de res|camaron|sin gluten de|garbanzo|lenteja|fideos de huevo|blue dragon")],
  },
  {
    slug: "papel-higienico", label: "Papel higiénico", profecoName: "Papel higiénico", pacic: true, glyph: "🧻",
    unit: "pz", typicalPack: 4, searchTerms: ["higienico", "papel de bano"],
    include: [w("papel higienico|higienico|papel de bano")],
    exclude: [w("humed[oa]s?|toallitas|portarrollos?|porta|dispensador|despachador|soporte|juguete|cubierta|desechable para asiento|cubreasientos")],
  },
  {
    slug: "tortilla", label: "Tortilla de maíz", profecoName: "Tortilla de maíz de supermercado", pacic: true, glyph: "🫓",
    unit: "kg", typicalPack: 1, searchTerms: ["tortilla"],
    include: [/\btortillas?\b.*\b(?:maiz|nixtamal|nixtamalizad[oa]|blancas?|amarillas?|azules?)\b|\b(?:maiz|nixtamal)\b.*\btortillas?\b/],
    exclude: [w("harina|trigo|integral|arroz|chips|tostadas?|totopos?|tortiller[oa]|prensa|calentador|comal|ezekiel|wraps?|flour|harina de maiz|maseca|minsa|nopal|linaza|chia|tortilla de harina|burritos?|tacos? congelados|para tacos dorados|doritos|flamin|totis|sabritas|chips")],
  },
  // ── Frutas y verduras frescas ───────────────────────────────────────────
  {
    slug: "jitomate", label: "Jitomate saladet", profecoName: "Jitomate saladet", pacic: true, glyph: "🍅",
    unit: "kg", typicalPack: 1, searchTerms: ["jitomate", "tomate"], produce: true,
    include: [w("jitomates?|tomates?")],
    exclude: [w("tomates? verdes?|verde|tomatillo|sardina|atun")],
  },
  {
    slug: "cebolla", label: "Cebolla blanca", profecoName: "Cebolla blanca", pacic: true, glyph: "🧅",
    unit: "kg", typicalPack: 1, searchTerms: ["cebolla"], produce: true,
    include: [w("cebollas?")],
    exclude: [w("aros|cambray en vinagre|ajo y cebolla")],
  },
  {
    slug: "chile-jalapeno", label: "Chile jalapeño", profecoName: "Chile jalapeño", pacic: true, glyph: "🌶️",
    unit: "kg", typicalPack: 1, searchTerms: ["jalapeno", "cuaresmeno"], produce: true,
    include: [w("jalapenos?|chile cuaresmeno|cuaresmenos?")],
    exclude: [w("queso|relleno|nachos|toreados en|clemente jacques|la costena|san marcos|herdez|embasa|dona maria")],
  },
  {
    slug: "papa", label: "Papa blanca", profecoName: "Papa blanca", pacic: true, glyph: "🥔",
    unit: "kg", typicalPack: 1, searchTerms: ["papa"], produce: true,
    include: [/^(?:verdura )?papas?\b/, /\bpapa (?:blanca|alfa|russet|cambray|morena|amarilla|roja)\b/],
    exclude: [w("gajo|gajos|hash|a la francesa|mccain|ruffles|doritos|tostitos|instantane[oa]|almidon|fecula|aceite aguacate")],
  },
  {
    slug: "zanahoria", label: "Zanahoria", profecoName: "Zanahoria", pacic: true, glyph: "🥕",
    unit: "kg", typicalPack: 1, searchTerms: ["zanahoria"], produce: true,
    include: [w("zanahorias?")],
    exclude: [w("pastel|rallada en lata")],
  },
  {
    slug: "limon", label: "Limón", profecoName: "Limón", pacic: true, glyph: "🍋",
    unit: "kg", typicalPack: 1, searchTerms: ["limon"], produce: true,
    include: [w("limon|limones")],
    exclude: [w("fabuloso|axion|salvo|ajax|pinol|sal con limon|limon y sal|pimienta|cerveza|electrolit|suero|kombucha|vinagreta|te de limon")],
  },
  {
    slug: "manzana", label: "Manzana", profecoName: "Manzana", pacic: true, glyph: "🍎",
    unit: "kg", typicalPack: 1, searchTerms: ["manzana"], produce: true,
    include: [w("manzanas?")],
    exclude: [w("sidra|manzanita|apple|chips de manzana|canela y manzana")],
  },
  {
    slug: "platano", label: "Plátano", profecoName: "Plátano", pacic: true, glyph: "🍌",
    unit: "kg", typicalPack: 1, searchTerms: ["platano", "banana"], produce: true,
    include: [w("platanos?|bananas?")],
    exclude: [w("licuado|split|frit[oa]s?|machitos|harina de platano")],
  },
];

/** Complementos de SuCom (NO son PACIC): sólo se usan para armar canastas. */
export const COMPLEMENT_CONCEPTS: BasketConcept[] = [
  {
    slug: "cafe", label: "Café", pacic: false, glyph: "☕", unit: "kg", typicalPack: 0.2, searchTerms: ["cafe"],
    include: [/\bcafe\b.*\b(?:soluble|molido|en grano|tostado|instantaneo|clasico|descafeinado|americano|de olla)\b/, /\b(?:nescafe|legal|cafe la finca|punta del cielo|cafe combate|garat|cafe internacional)\b/],
    exclude: [w("capsulas?|k cup|dolce gusto|nespresso|cafetera|molino|taza|filtro|sabor|crema para cafe|coffee mate|frappe|helado|galletas?|chocolate|licor|lata|listo para beber|bebida|cold brew|shampoo|exfoliante|scrub|mascarilla|vela|con azucar|concentrate|concentrado|espresso|ml|sobres?|sticks?|capuchino|cappuccino|latte|mocha|vainilla|caramelo")],
  },
  {
    slug: "sal", label: "Sal de mesa", pacic: false, glyph: "🧂", unit: "kg", typicalPack: 1, searchTerms: ["sal "],
    include: [/^sal\b(?! con)|\bsal (?:de mesa|refinada|yodada|fina|de grano|marina|del mar|la fina|elefante)\b/],
    exclude: [w("sales de bano|sal de uvas|sal con limon|sal de ajo|sal de cebolla|salsa|salado|salchicha|sal y pimienta|electrolitos|mineral|epsom|para alberca|suavizador|sal de higuera|exfoliante|lampara|salero|molinillo|molino|manantial|ahumada|parrillera|con chile|chile|limon|ajo|hierbas|trufa|negra|rosa del himalaya|escarchar")],
  },
  {
    slug: "avena", label: "Avena", pacic: false, glyph: "🥣", unit: "kg", typicalPack: 0.4, searchTerms: ["avena"],
    include: [/\bavena\b.*\b(?:en hojuelas|hojuelas|natural|integral|tradicional|instantanea|quaker|3 minutos|entera|organica)\b|^avena\b/],
    exclude: [w("bebida|leche|galletas?|barra|cereal con|granola|sabor|shampoo|jabon|crema|mascarilla|exfoliante|bano|perros?|harina|con manzana|con fresa|con chocolate|lista|arandanos|fresa|manzana|platano|canela|nuez|chia|linaza|matcha|unicornio|proteina|0 azucar")],
  },
  {
    slug: "detergente", label: "Detergente para ropa", pacic: false, glyph: "🧴", unit: "kg", typicalPack: 1, searchTerms: ["detergente"],
    include: [/\bdetergente\b.*\b(?:ropa|polvo|liquido|lavadora|ariel|persil|ace|roma|foca|mas color|carisma|finish|bold|blanca nieves|great value|1 2 3|punto blanco|vel rosita)\b|^detergente\b/],
    exclude: [w("lavatrastes|trastes|lavavajillas|finish|pastillas lavavajillas|ropa de bebe vel|auto|multiusos|piso|pisos|banos")],
  },
  {
    slug: "cloro", label: "Cloro", pacic: false, glyph: "🫧", unit: "L", typicalPack: 1, searchTerms: ["cloro", "blanqueador"],
    include: [/^cloro\b|\bcloro\b.*\b(?:cloralex|clorox|great value|blanqueador|desinfectante|liquido|gel)\b|\b(?:cloralex|clorox)\b|\bblanqueador\b.*\b(?:cloro|ropa)\b/],
    exclude: [w("pastillas para alberca|alberca|tabletas|toallitas|aerosol|spray|sin cloro|tiras|kit|medidor|colores|desmanchador|quitamanchas")],
  },
  {
    slug: "lavatrastes", label: "Lavatrastes", pacic: false, glyph: "🍽️", unit: "L", typicalPack: 0.75, searchTerms: ["lavatrastes", "lavaplatos", "salvo", "axion"],
    include: [/\blavatrastes\b|\blavaplatos\b|\b(?:salvo|axion|dawn|ajax)\b.*\b(?:liquido|lavatrastes|trastes|limon|ml|l)\b/],
    exclude: [w("esponjas?|fibras?|cepillos?|guantes?|escurridor|dispensador|lavavajillas|maquina|pastillas|capsulas|tabletas")],
  },
  {
    slug: "pasta-dental", label: "Pasta dental", pacic: false, glyph: "🪥", unit: "pz", typicalPack: 1, searchTerms: ["pasta dental", "crema dental", "dentifrico"],
    include: [w("pasta dental|crema dental|dentifrico")],
    exclude: [w("cepillo|hilo dental|enjuague|kit de viaje|blanqueamiento kit|para perro|infantil juguete")],
  },
];

export const ALL_CONCEPTS: BasketConcept[] = [...PACIC_CONCEPTS, ...COMPLEMENT_CONCEPTS];

export interface ClassifiableProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  soldByWeight?: boolean | null;
}

export function conceptMatches(concept: BasketConcept, product: ClassifiableProduct): boolean {
  const name = normalizeForMatch(product.name);
  const brand = normalizeForMatch(product.brand ?? "");
  const category = normalizeForMatch(product.category ?? "");
  // Algunas tiendas cortan el sustantivo al campo marca
  // ("En Salsa De Tomate Dolores Lata 425g" + marca "Sardina").
  const text = brand && !name.includes(brand) ? `${name} ${brand}` : name;
  const brandFirst = brand ? `${brand} ${name}` : name;

  if (GLOBAL_EXCLUDE.test(text)) return false;
  if (category && GLOBAL_EXCLUDE_CATEGORY.test(category)) return false;
  if (!concept.include.some((re) => re.test(text) || re.test(brandFirst))) return false;
  if (concept.exclude?.some((re) => re.test(text))) return false;

  if (concept.produce) {
    if (PROCESSED.test(text)) return false;
    const head = new RegExp(`^(?:(?:fruta|verdura|chile|tomate|bolsa de|malla de)s? )?(?:${concept.searchTerms.join("|")})`);
    const freshCategory = /\b(?:frutas?|verduras?|hortalizas?|legumbres|frescos?)\b/.test(category);
    const looksFresh = head.test(name) || head.test(brandFirst) || BULK_PRODUCE.test(text) || freshCategory || !!product.soldByWeight;
    if (!looksFresh) return false;
  }
  return true;
}

/** Devuelve el concepto PACIC (uno de los 24) que corresponde al producto, o null. */
export function classifyPacic(product: ClassifiableProduct): BasketConcept | null {
  for (const concept of PACIC_CONCEPTS) {
    if (conceptMatches(concept, product)) return concept;
  }
  return null;
}

/** Igual que `classifyPacic` pero también reconoce los complementos. */
export function classifyConcept(product: ClassifiableProduct): BasketConcept | null {
  for (const concept of ALL_CONCEPTS) {
    if (conceptMatches(concept, product)) return concept;
  }
  return null;
}

export function findConcept(slug: string): BasketConcept | undefined {
  return ALL_CONCEPTS.find((c) => c.slug === slug);
}
